import { useState } from "react";
import { Search, UserPlus, Pencil, Users, Star, Crown, AlertTriangle } from "lucide-react";
import { ROLE_LABELS } from "@/lib/roles";
import { EmptyState } from "@/components/admin/ui";

const initials = (nameOrEmail) => (nameOrEmail || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

export const MembersPanel = ({
  team,
  isAll,
  members,
  memberCount,
  leadCount,
  solutionCount,
  pendingInvitations,
  selectedMemberId,
  onSelectMember,
  onInviteNew,
  eligibleToAdd,
  onAddExisting,
  savingUserId,
  onEditTeam,
  onPromoteToLead,
}) => {
  const [query, setQuery] = useState("");
  const filtered = members.filter((m) => {
    const q = query.trim().toLowerCase();
    return !q || (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q);
  });
  const promotable = !isAll ? members.filter((m) => m.role !== "team_lead" && !m.is_owner) : [];

  return (
    <div className="flex h-full min-h-0 flex-col rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-[#111C33]" data-testid="org-members-panel">
      <div className="border-b border-slate-100 p-4 dark:border-white/10">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-base font-bold text-slate-950 dark:text-white" data-testid="org-members-panel-title">
                {isAll ? "All Members" : team?.name}
              </h2>
              {!isAll && (
                <button type="button" onClick={onEditTeam} data-testid="org-members-edit-team-btn" className="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400">
                  <Pencil size={13} />
                </button>
              )}
            </div>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400" data-testid="org-members-panel-subtitle">
              {isAll ? "Everyone across your organization." : team?.description || "No description yet."}
            </p>
          </div>
          <button
            type="button"
            onClick={onInviteNew}
            data-testid="org-members-add-member-btn"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-200 hover:bg-[#0041CC]"
          >
            <UserPlus size={14} /> Add Member
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="org-members-stats">
          <span className="rounded-lg bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-600 dark:bg-white/5 dark:text-slate-300">{memberCount} Member{memberCount === 1 ? "" : "s"}</span>
          <span className="rounded-lg bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-600 dark:bg-white/5 dark:text-slate-300">{leadCount} Team Lead{leadCount === 1 ? "" : "s"}</span>
          <span className="rounded-lg bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-600 dark:bg-white/5 dark:text-slate-300">{solutionCount} Assigned Solution{solutionCount === 1 ? "" : "s"}</span>
        </div>
        {!isAll && team && !team.has_active_lead && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="org-members-no-active-lead-warning">
            <span className="flex items-center gap-1.5"><AlertTriangle size={13} className="shrink-0" /> This team has no Team Lead.</span>
            {promotable.length > 0 && (
              <select
                value=""
                disabled={!!savingUserId}
                onChange={(e) => e.target.value && onPromoteToLead(e.target.value)}
                data-testid="org-members-promote-lead-select"
                className="rounded-lg border border-amber-300 bg-white px-2 py-1 text-xs font-bold text-amber-700 dark:border-amber-500/30 dark:bg-[#111C33] dark:text-amber-400"
              >
                <option value="">Promote to Team Lead…</option>
                {promotable.map((m) => (
                  <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
                ))}
              </select>
            )}
          </div>
        )}
        <div className="mt-3 flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search members"
              data-testid="org-members-search-input"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-8 pr-3 text-xs font-semibold text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
            />
          </div>
          {!isAll && eligibleToAdd.length > 0 && (
            <select
              value=""
              disabled={!!savingUserId}
              onChange={(e) => e.target.value && onAddExisting(e.target.value)}
              data-testid="org-members-add-existing-select"
              className="rounded-lg border border-dashed border-slate-300 px-2 py-2 text-xs font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white"
            >
              <option value="">+ Add existing</option>
              {eligibleToAdd.map((m) => (
                <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {pendingInvitations.length > 0 && (
        <div className="border-b border-slate-100 bg-amber-50/50 p-3 dark:border-white/10 dark:bg-amber-500/5" data-testid="org-members-pending-invitations">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-amber-600 dark:text-amber-400">Pending Invitations ({pendingInvitations.length})</p>
          <div className="space-y-1">
            {pendingInvitations.map((inv) => (
              <div key={inv.invite_id} className="flex items-center justify-between text-xs" data-testid={`org-members-pending-${inv.invite_id}`}>
                <span className="font-semibold text-slate-700 dark:text-slate-300">{inv.email}</span>
                <span className="text-slate-400">{ROLE_LABELS[inv.role] || inv.role}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2" data-testid="org-members-list">
        {filtered.length === 0 ? (
          <EmptyState icon={Users} title="No members here." message="Add or invite a member to get started." testId="org-members-empty" />
        ) : (
          filtered.map((m) => (
            <button
              key={m.user_id}
              type="button"
              onClick={() => onSelectMember(m.user_id)}
              data-testid={`org-member-item-${m.user_id}`}
              className={`mb-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-200 ${
                selectedMemberId === m.user_id ? "bg-[#0052FF]/10 dark:bg-[#0052FF]/20" : "hover:bg-slate-50 dark:hover:bg-white/5"
              }`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0052FF] to-[#00D4FF] text-xs font-extrabold text-white">
                {initials(m.name || m.email)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-bold text-slate-950 dark:text-white">{m.name || m.email}</span>
                  {m.teams?.some((t) => t.is_lead) && <Star size={11} className="shrink-0 text-amber-500" fill="currentColor" />}
                  {m.is_owner && <Crown size={11} className="shrink-0 text-violet-500" fill="currentColor" />}
                </span>
                <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{m.email}</span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-[11px] font-bold text-slate-400 dark:text-slate-500">{ROLE_LABELS[m.role] || m.role}</span>
                {m.teams?.length > 0 && (
                  <span className="block text-[10px] text-slate-400 dark:text-slate-500" data-testid={`org-member-item-team-${m.user_id}`}>{m.teams.map((t) => t.name).join(", ")}</span>
                )}
                {m.is_deactivated && <span className="mt-0.5 block text-[10px] font-bold text-red-500">Deactivated</span>}
                {m.incomplete_access && <span className="mt-0.5 block text-[10px] font-bold text-amber-500">Incomplete</span>}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};
